import { Badge } from "design-system/atoms/Badge"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "design-system/atoms/Sheet"
import { cn } from "design-system/utils"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"

const MAX_WIDTH_CLASS: Record<string, string> = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  badge?: ReactNode
  title: string
  description?: string
  showOverrideHint?: boolean
  maxWidth?: "sm" | "md" | "lg" | "xl"
  children: ReactNode
}

export function CapabilitySheetLayout({
  open,
  onOpenChange,
  badge,
  title,
  description,
  showOverrideHint,
  maxWidth = "xl",
  children,
}: Props) {
  const { t } = useTranslation()
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className={cn(
          "flex h-full w-full flex-col gap-0 overflow-hidden p-0",
          MAX_WIDTH_CLASS[maxWidth],
        )}
      >
        <SheetHeader className="flex shrink-0 flex-col gap-2 space-y-0 border-b bg-muted/40 p-5 text-center items-center sm:items-start sm:text-left">
          <div className="flex w-full items-center justify-center sm:justify-start">{badge}</div>
          <SheetTitle>{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
          {showOverrideHint && (
            <Badge variant="secondary" className="self-center sm:self-start">
              {t("capabilities.activation.overrideHint")}
            </Badge>
          )}
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      </SheetContent>
    </Sheet>
  )
}
